export interface ApiOk<T> {
  ok: true;
  data: T;
}
export interface ApiErr {
  ok: false;
  error: { code: string; message?: string };
}
export type ApiResponse<T> = ApiOk<T> | ApiErr;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
  ) {
    super(message ?? `${status} ${code}`);
  }
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };
  const res = await fetch(path, init);
  let parsed: unknown = null;
  try {
    parsed = await res.json();
  } catch {
    /* empty body - leave parsed null */
  }
  if (!res.ok) {
    // Náš tvar: {ok:false, error:{code,message}}. NestJS HttpException ale vrací
    // {statusCode, message, error}. Přečteme obojí, ať se uživateli ukáže důvod
    // (ne generické „HTTP_400"). message může být string i pole (validace).
    const e = parsed as { error?: { code?: string; message?: string } | string; message?: string | string[] } | null;
    const code = (typeof e?.error === 'object' ? e.error.code : undefined) ?? `HTTP_${res.status}`;
    const rawMsg = (typeof e?.error === 'object' ? e.error.message : undefined) ?? e?.message;
    const message = Array.isArray(rawMsg) ? rawMsg.join(', ') : rawMsg;
    throw new ApiError(res.status, code, message);
  }
  // 204 No Content nemá tělo, takže parsed zůstane null. To je úspěch, ne chyba.
  if (res.status === 204) return undefined as T;
  const ok = parsed as ApiOk<T> | null;
  if (!ok?.ok) {
    throw new ApiError(res.status, 'INVALID_RESPONSE', 'API returned non-ok body');
  }
  return ok.data;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};
